import { Group, Layer, Rect, Stage, Image } from 'react-konva';
import React, { memo, useState } from 'react';
import { createPortal } from 'react-dom';
import DisableImage from '../plate/DisableImage';
import { CanvasTooltipProps } from 'components/canvas/tooltip';
import { useRMDispatch } from 'redux/hooks';
import { toggleWellIndependentZoom } from 'redux/reducers/wellReducer';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { DEFAULT_WELL_SCORE_SIZE } from '../../../constants/scoreConstants';

interface Props {
  wellNumber: string;
  wellInfo: string;
  wellScores: any;
  disableImagingIcon?: React.ReactNode;
  isImagingDisabled?: boolean;
  canvasWidth: number;
  selectedWellsLength?: number;
  wellKey?: string;
  isIndependentZoom?: boolean;
  hideContent?: boolean;
  isOverlayMode?: boolean;
  scoresRowHeight?: number;
}
const iconAndScoreSize = DEFAULT_WELL_SCORE_SIZE;
const syncZoomIcon = RMSvgIconImage({ name: 'SyncZoom', fill: 'white' });
const unSyncZoomIcon = RMSvgIconImage({ name: 'UnSyncZoom', fill: 'white' });
const overlayModeIcon = RMSvgIconImage({ name: 'OverlayMode', fill: '#414042' });
const WellCanvasInfoHeader = (props: Props) => {
  const {
    canvasWidth,
    wellInfo,
    wellNumber,
    wellScores,
    disableImagingIcon,
    isImagingDisabled,
    selectedWellsLength,
    wellKey,
    isIndependentZoom,
    hideContent = false,
    isOverlayMode = false,
    scoresRowHeight = iconAndScoreSize,
  } = props;
  const defaultTooltipState = {
    windowPointerPositionX: 0,
    windowPointerPositionY: 0,
    scale: 1,
    stage: null,
    toolTipText: null,
  };
  const [tooltipData, setTooltipData] = useState<CanvasTooltipProps>(defaultTooltipState);
  const dispatch = useRMDispatch();
  const isCompareModeWithMultipleWells =
    (window?.location?.pathname.includes('/compare') ||
      window?.location?.pathname.includes('/well')) &&
    selectedWellsLength > 1;
  const AllowDisputedScoreDrawing = useSystemProperty(
    PropertyConstants.AllowDisputedScoreDrawing,
    false,
  );
  const isAnyScoreDisputed = wellScores?.props?.scoresOfWellDrops?.some(
    (el) => el.isDisputed === true,
  );
  const scoresCount =
    (wellScores?.props?.scoresOfWellDrops?.length || 0) +
    (isAnyScoreDisputed && AllowDisputedScoreDrawing ? 1 : 0);
  const width = canvasWidth + (wellInfo ? canvasWidth * 0.01 : 0); //adjust width
  const scoresXWidthFactor = isCompareModeWithMultipleWells ? 0.49 : 0.5;
  const baseWidth = wellInfo ? width * scoresXWidthFactor : width * 0.79;

  const compareModeOffset = isCompareModeWithMultipleWells ? iconAndScoreSize : 0;
  const imagingOffset = disableImagingIcon || isImagingDisabled ? iconAndScoreSize : 0;
  const overlayModeOffset =
    isOverlayMode && !isCompareModeWithMultipleWells ? iconAndScoreSize : 0;

  const scoresX =
    baseWidth -
    scoresCount * iconAndScoreSize -
    compareModeOffset -
    imagingOffset -
    overlayModeOffset;

  function onMouseMove(evt) {
    evt.evt.preventDefault();
    const { clientX, clientY } = evt.evt;
    if (evt.target.attrs.tooltipInfo) {
      setTooltipData({
        windowPointerPositionX: clientX,
        windowPointerPositionY: clientY,
        scale: 1,
        stage: null,
        toolTipText: evt.target.attrs.tooltipInfo,
      });
    } else {
      if (tooltipData.toolTipText !== null) {
        setTooltipData(defaultTooltipState);
      }
    }
  }

  const getTooltipPosition = () => {
    const viewportWidth = window.innerWidth;

    let adjustedX = tooltipData.windowPointerPositionX - 35;
    const adjustedY = tooltipData.windowPointerPositionY + 20;

    if (adjustedX + 150 > viewportWidth) {
      adjustedX = viewportWidth - 150 - 10;
    }

    return { top: `${adjustedY}px`, left: `${adjustedX}px` };
  };

  const getIcon = () => {
    const baseX = width * scoresXWidthFactor - iconAndScoreSize;

    if (!isOverlayMode && !(isCompareModeWithMultipleWells && wellKey)) {
      return null;
    }

    const tooltip = isOverlayMode
      ? 'Overlay Mode'
      : isIndependentZoom
      ? 'Sync Zoom'
      : 'Unsync Zoom';

    const iconImage = isOverlayMode
      ? overlayModeIcon
      : isIndependentZoom
      ? unSyncZoomIcon
      : syncZoomIcon;

    return (
      <Layer
        id="wellcanvasinfoheader-canvas-stage-layer"
        x={baseX}
        onClick={
          isOverlayMode ? undefined : () => dispatch(toggleWellIndependentZoom(wellKey))
        }
      >
        <Group id="wellcanvasinfoheader-canvas-stage-layer-group">
          <Rect
            id="wellcanvasinfoheader-canvas-stage-layer-rect"
            width={iconAndScoreSize}
            height={iconAndScoreSize}
            fill="gray"
            cornerRadius={5}
            tooltipInfo={tooltip}
          />
          <Image
            id="wellcanvasinfoheader-canvas-stage-layer-image"
            image={iconImage}
            width={iconAndScoreSize}
            height={iconAndScoreSize}
            tooltipInfo={tooltip}
          />
        </Group>
      </Layer>
    );
  };

  return (
    <div
      id="wellcanvasinfoheader-root"
      className={`flex items-center justify-between bg-white border-solid border-[2px] border-[lightgray] ${
        hideContent ? 'opacity-0' : ''
      }`}
      style={{ width }}
    >
      <div
        id="wellcanvasinfoheader-info-root"
        className={`flex flex-row items-center h-[${iconAndScoreSize}px] ${
          wellInfo ? 'w-[51%]' : 'w-[20%]'
        }`}
      >
        <div
          id="wellcanvasinfoheader-well-number"
          className="font-bold pl-[1%] pr-[1%] whitespace-nowrap"
        >
          {wellNumber}
        </div>
        <div
          id="wellcanvasinfoheader-separator"
          className={`[border-left:2px_solid_gray] mr-[1%] h-[${iconAndScoreSize}px]`}
        ></div>
        <div
          id="wellcanvasinfoheader-well-info-text"
          className="truncate"
          title={wellInfo}
        >
          {wellInfo}
        </div>
      </div>
      <div
        id="wellcanvasinfoheader-canvas-root"
        className={`relative z-[5] overflow-visible h-[${iconAndScoreSize}px] max-h-[${iconAndScoreSize}px] ${
          wellInfo ? 'w-[51%]' : 'w-[80%]'
        }`}
      >
        <Stage
          id={'wellcanvasinfoheader-stageInfo'}
          width={wellInfo ? width / 2 : width * 0.79}
          height={scoresRowHeight}
          draggable={false}
          x={0} // workaround for compare route issue with canvas width and x coords
          onMouseMove={onMouseMove}
          onMouseOut={() => setTooltipData(defaultTooltipState)}
        >
          <Layer id="wellcanvasinfoheader-canvas-stage-layer-score" x={scoresX}>
            {wellScores}
          </Layer>
          {getIcon()}
          {/**
           * // incorporate disableimage coords if icon exists
           */}
          <Layer
            id="wellcanvasinfoheader-canvas-stage-layer-compare-mode-imaging"
            x={
              width * scoresXWidthFactor -
              (isCompareModeWithMultipleWells || isOverlayMode
                ? 2 * iconAndScoreSize
                : iconAndScoreSize)
            }
          >
            {disableImagingIcon && disableImagingIcon}
            {isImagingDisabled && <DisableImage x={0} y={0} width={32} height={32} />}
          </Layer>
        </Stage>
      </div>
      <div
        id="wellcanvasinfoheader-tooltip-root"
        className="well-info-header-tooltip-root"
        style={getTooltipPosition()}
      >
        {tooltipData?.toolTipText &&
          createPortal(
            <div
              id="wellcanvasinfoheader-tooltip-root"
              className="well-info-header-tooltip"
              style={getTooltipPosition()}
            >
              {tooltipData.toolTipText.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default memo(WellCanvasInfoHeader);
